import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getProducts, getSites, getProductStock, getProductSubstitutions,
  findSubstitutes, getProductCategories, getProductAllergens,
  getProductLifecycleStages, getProductFreeFromTags, getProductDietaryTags,
  getUsers, addOrderLine, addPackToOrder,
} from '../api';

// =====================================================
// All known allergens for badge colouring
// =====================================================
const ALLERGEN_COLOURS = {
  Gluten: '#d97706',      Milk: '#2563eb',      Eggs: '#dc2626',
  Fish: '#0891b2',        Crustaceans: '#e11d48', Soya: '#65a30d',
  Sesame: '#9333ea',      Celery: '#059669',     Nuts: '#b45309',
  Peanuts: '#92400e',     Mustard: '#ca8a04',    Lupin: '#7c3aed',
  Molluscs: '#0d9488',    Sulphites: '#6366f1',
};

export default function ProductsPage() {
  const [searchParams] = useSearchParams();

  // ---- session ----
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);

  // ---- data ----
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allFreeFromTags, setAllFreeFromTags] = useState([]);
  const [allDietaryTags, setAllDietaryTags] = useState([]);
  const [lifecycleStages, setLifecycleStages] = useState([]);

  // ---- pagination ----
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 1, pageSize: 50 });
  const [loading, setLoading] = useState(false);

  // ---- filters ----
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | product | pack

  const [selectedFreeFrom, setSelectedFreeFrom] = useState([]);
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);

  // ---- detail panel ----
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [substitutions, setSubstitutions] = useState([]);

  // ---- pack expansion ----
  const [expandedPacks, setExpandedPacks] = useState(new Set());

  // ---- substitute tabs ----
  const [subTab, setSubTab] = useState('recommended'); // 'recommended' | 'search'
  const [subResults, setSubResults] = useState(null);
  const [subSearching, setSubSearching] = useState(false);
  const [subCriteria, setSubCriteria] = useState({
    maxPriceDelta: 5, avoidAllergens: [], matchIngredients: [],
    inStockOnly: true, category: '',
  });

  // ---- order builder ----
  const [activeOrderId, setActiveOrderId] = useState('');
  const [addingLine, setAddingLine] = useState(false);
  const [lineQty, setLineQty] = useState(1);
  const [lineMessage, setLineMessage] = useState(null);

  // ---- load initial data ----
  useEffect(() => {
    Promise.all([getSites(), getProductCategories(), getProductAllergens(), getProductLifecycleStages(), getProductFreeFromTags(), getProductDietaryTags(), getUsers()])
      .then(([s, c, _a, ls, ff, dt, u]) => {
        setSites(s.data);
        setCategories(c.data);
        setLifecycleStages(ls.data);
        setAllFreeFromTags(ff.data);
        setAllDietaryTags(dt.data);
        setUsers(u.data);

        // If launched from HubSpot with query params, use them
        const urlUserId = searchParams.get('userId');
        const urlOrderId = searchParams.get('orderId');

        if (urlUserId && u.data.length > 0) {
          const matched = u.data.find(usr => String(usr.UserId) === urlUserId);
          if (matched) setCurrentUser(matched);
          else setCurrentUser(u.data[0]);
        } else if (u.data.length > 0 && !currentUser) {
          setCurrentUser(u.data[0]);
        }

        if (urlOrderId) {
          setActiveOrderId(urlOrderId);
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAbortRef = useRef(null);

  // ---- fetch products on filter change ----
  const fetchProducts = useCallback(async (requestedPage) => {
    // Cancel any in-flight request
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    const p = requestedPage || page;
    const params = { page: p, pageSize: 50 };
    if (search) params.search = search;
    if (categoryFilter) params.category = categoryFilter;
    if (lifecycleFilter) params.lifecycleStage = lifecycleFilter;
    if (typeFilter !== 'all') params.type = typeFilter;
    if (selectedFreeFrom.length > 0) params.freeFrom = selectedFreeFrom.join(',');
    if (selectedDietary.length > 0) params.dietary = selectedDietary.join(',');
    if (currentUser) params.siteId = currentUser.SiteId;
    if (inStockOnly) params.inStock = 'true';
    params.includeStock = 'true';

    setLoading(true);
    try {
      const res = await getProducts(params, controller.signal);
      // Support both old flat-array and new { data, pagination } format
      if (res.data && res.data.pagination) {
        setProducts(res.data.data);
        setPagination(res.data.pagination);
      } else {
        setProducts(Array.isArray(res.data) ? res.data : []);
        setPagination({ totalItems: (Array.isArray(res.data) ? res.data : []).length, totalPages: 1, pageSize: 50 });
      }
    } catch (e) {
      if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') console.error(e);
    }
    setLoading(false);
  }, [page, search, categoryFilter, lifecycleFilter, typeFilter, selectedFreeFrom, selectedDietary, inStockOnly, currentUser]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, lifecycleFilter, typeFilter, selectedFreeFrom, selectedDietary, inStockOnly, currentUser]);

  // Fetch products on mount
  useEffect(() => {
    fetchProducts(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch on filter or page change (debounced)
  useEffect(() => {
    const t = setTimeout(() => fetchProducts(), 400);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  // ---- select product ----
  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    setSubTab('recommended');
    setSubResults(null);
    setLineMessage(null);

    // Packs don't have stock or substitution data
    if (product._type === 'pack') {
      setStockData([]);
      setSubstitutions([]);
      return;
    }

    const siteId = currentUser?.SiteId || undefined;
    try {
      const [stockRes, subsRes] = await Promise.all([
        getProductStock(product.ProductId, siteId),
        getProductSubstitutions(product.ProductId, siteId)
      ]);
      setStockData(stockRes.data);
      setSubstitutions(subsRes.data);
    } catch (e) { console.error(e); }
  };

  // ---- toggle pack expansion ----
  const positionPopup = (e) => {
    const popup = e.currentTarget.querySelector('.tag-hover-popup');
    if (!popup) return;
    const r = e.currentTarget.getBoundingClientRect();
    popup.style.left = (r.left + r.width / 2) + 'px';
    popup.style.bottom = (window.innerHeight - r.top + 6) + 'px';
    popup.style.transform = 'translateX(-50%)';
  };

  const togglePackExpanded = (packId) => {
    setExpandedPacks(prev => {
      const next = new Set(prev);
      if (next.has(packId)) next.delete(packId);
      else next.add(packId);
      return next;
    });
  };

  // ---- substitute finder ----
  const handleFindSubstitutes = async () => {
    if (!selectedProduct) return;
    setSubSearching(true);
    try {
      const res = await findSubstitutes(selectedProduct.ProductId, {
        siteId: currentUser?.SiteId || null,
        ...subCriteria,
      });
      setSubResults(res.data);
    } catch (e) { console.error(e); }
    setSubSearching(false);
  };

  // ---- add to order ----
  const handleAddToOrder = async (product) => {
    if (!activeOrderId) {
      setLineMessage({ type: 'error', text: 'Enter an Order ID first' });
      return;
    }
    setAddingLine(true);
    try {
      if (product._type === 'pack') {
        await addPackToOrder(parseInt(activeOrderId), product.PackId, lineQty);
        setLineMessage({ type: 'success', text: `Added ${lineQty}× ${product.ProductName} (pack) to order #${activeOrderId}` });
      } else {
        await addOrderLine(parseInt(activeOrderId), {
          ProductId: product.ProductId,
          Quantity: lineQty,
          UnitPrice: product.UnitPrice,
        });
        setLineMessage({ type: 'success', text: `Added ${lineQty}× ${product.ProductName} to order #${activeOrderId}` });
      }
      setLineQty(1);
    } catch (e) {
      setLineMessage({ type: 'error', text: e.response?.data?.error || e.message });
    }
    setAddingLine(false);
  };


  // ---- toggle free-from in filter ----
  const toggleFreeFrom = (tag) => {
    setSelectedFreeFrom(prev =>
      prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]
    );
  };
  // ---- toggle dietary in filter ----
  const toggleDietary = (tag) => {
    setSelectedDietary(prev =>
      prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]
    );
  };



  // ---- user site display ----
  const userSite = currentUser ? sites.find(s => s.SiteId === currentUser.SiteId) : null;

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <>
      <div className="top-header">
        <h1>Products</h1>
        <div className="top-header-actions" style={{ gap: 12, display: 'flex', alignItems: 'center' }}>
          {/* User selector */}
          <select className="hs-select" style={{ width: 200 }}
            value={currentUser?.UserId || ''}
            onChange={e => {
              const u = users.find(u => u.UserId === parseInt(e.target.value));
              setCurrentUser(u || null);
              setSelectedProduct(null);
            }}>
            <option value="">Select User</option>
            {users.map(u => (
              <option key={u.UserId} value={u.UserId}>{u.DisplayName} — {u.Site?.SiteName || 'No site'}</option>
            ))}
          </select>
          {userSite && (
            <span className="hs-badge hs-badge-available">{userSite.SiteCode}</span>
          )}
        </div>
      </div>

      <div className="page-content">
        {/* ---- SEARCH & FILTER BAR ---- */}
        <div className="hs-card mb-md">
          <div className="hs-card-body">
            <div className="hs-form-row" style={{ alignItems: 'flex-end' }}>
              <div className="hs-form-group" style={{ flex: 2 }}>
                <label>Search</label>
                <input className="hs-input" placeholder="Search by name, SKU, description, or ingredient…"
                  value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div className="hs-form-group" style={{ flex: 1 }}>
                <label>Type</label>
                <select className="hs-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="all">All Items</option>
                  <option value="product">Products Only</option>
                  <option value="pack">Packs Only</option>
                </select>
              </div>
              <div className="hs-form-group" style={{ flex: 1 }}>
                <label>Category</label>
                <select className="hs-select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="hs-form-group" style={{ flex: 1 }}>
                <label>Lifecycle Stage</label>
                <select className="hs-select" value={lifecycleFilter} onChange={e => setLifecycleFilter(e.target.value)}>
                  <option value="">All Stages</option>
                  {lifecycleStages.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="hs-form-group" style={{ flex: 0 }}>
                <label style={{ whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={inStockOnly} onChange={e => setInStockOnly(e.target.checked)} style={{ marginRight: 6 }} />
                  In stock only
                </label>
              </div>
            </div>


            {/* Recipe Doesn't Include filter chips */}
            {allFreeFromTags.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <span className="text-muted text-sm" style={{ marginRight: 8 }}>Recipe Doesn't Include:</span>
                {allFreeFromTags.map(tag => (
                  <button key={tag}
                    className={`hs-btn hs-btn-sm ${selectedFreeFrom.includes(tag) ? 'hs-btn-primary' : 'hs-btn-secondary'}`}
                    style={{ marginRight: 4, marginBottom: 4 }}
                    onClick={() => toggleFreeFrom(tag)}>
                    {selectedFreeFrom.includes(tag) ? '✓ ' : ''}{tag}
                  </button>
                ))}
                {selectedFreeFrom.length > 0 && (
                  <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => setSelectedFreeFrom([])}
                    style={{ marginLeft: 8 }}>Clear all</button>
                )}
              </div>
            )}

            {/* Dietary filter chips */}
            {allDietaryTags.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <span className="text-muted text-sm" style={{ marginRight: 8 }}>Dietary:</span>
                {allDietaryTags.map(tag => (
                  <button key={tag}
                    className={`hs-btn hs-btn-sm ${selectedDietary.includes(tag) ? 'hs-btn-primary' : 'hs-btn-secondary'}`}
                    style={{ marginRight: 4, marginBottom: 4 }}
                    onClick={() => toggleDietary(tag)}>
                    {selectedDietary.includes(tag) ? '✓ ' : ''}{tag}
                  </button>
                ))}
                {selectedDietary.length > 0 && (
                  <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => setSelectedDietary([])}
                    style={{ marginLeft: 8 }}>Clear all</button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ---- ORDER BUILDER BAR ---- */}
        <div className="hs-card mb-md" style={{ background: '#fdf4f0' }}>
          <div className="hs-card-body flex-between" style={{ padding: '10px 16px' }}>
            <div className="flex gap-sm" style={{ alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>📋 Order Builder</span>
              <input className="hs-input" style={{ width: 100 }} placeholder="Order ID"
                value={activeOrderId} onChange={e => setActiveOrderId(e.target.value)} />
              <input className="hs-input" style={{ width: 60 }} type="number" min="1"
                value={lineQty} onChange={e => setLineQty(Math.max(1, parseInt(e.target.value) || 1))} />
              {selectedProduct && (
                <button className="hs-btn hs-btn-primary hs-btn-sm" disabled={addingLine}
                  onClick={() => handleAddToOrder(selectedProduct)}>
                  {addingLine ? 'Adding…' : `+ Add ${selectedProduct.ProductName}`}
                </button>
              )}
            </div>
            {lineMessage && (
              <span className={lineMessage.type === 'error' ? 'text-danger' : 'text-success'} style={{ fontSize: 13 }}>
                {lineMessage.text}
              </span>
            )}
          </div>
        </div>

        {/* ---- MAIN LAYOUT: Product list + Detail panel ---- */}
        <div style={{ display: 'grid', gridTemplateColumns: selectedProduct ? '1fr 1fr' : '1fr', gap: 20 }}>

          {/* ======== PRODUCT LIST ======== */}
          <div className="hs-card">
            <div className="hs-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Product Catalog ({pagination.totalItems})</h2>
              {loading && <span className="text-muted text-sm">Loading…</span>}
            </div>
            <div style={{ maxHeight: 'calc(100vh - 370px)', overflow: 'auto' }}>
              <table className="hs-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Recipe Doesn't Include</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40 }}>
                      <div className="empty-state" style={{ padding: 0 }}>
                        <div className="empty-icon">🔍</div>
                        <h3>No products match your filters</h3>
                        <p>Try adjusting your search or "Recipe Doesn't Include" filters</p>
                      </div>
                    </td></tr>
                  ) : products.flatMap(p => {
                    const isPack = p._type === 'pack';
                    const avail = p.StockAtSite?.AvailableQty;
                    const isExpanded = isPack && expandedPacks.has(p.PackId);
                    const isSelected = selectedProduct?.ProductId === p.ProductId;

                    const mainRow = (
                      <tr key={p.ProductId} className="clickable"
                        onClick={() => {
                          handleSelectProduct(p);
                          if (isPack) togglePackExpanded(p.PackId);
                        }}
                        style={isSelected ? { background: '#fff1ed' } : {}}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {isPack && (
                              <span style={{ fontSize: 12, color: '#64748b', userSelect: 'none' }}>
                                {isExpanded ? '▼' : '▶'}
                              </span>
                            )}
                            <div>
                              <strong>
                                {isPack && <span title="Pack" style={{ marginRight: 4 }}>📦</span>}
                                {p.ProductName}
                              </strong>
                              {isPack && p.PromoPack ? (
                                <div style={{ marginTop: 2 }}>
                                  <span className="hs-badge" style={{ background: '#15803d', color: '#fff', fontSize: 9 }}>🎁 Promo</span>
                                </div>
                              ) : null}
                              <div className="text-muted text-sm font-mono">{p.SKU}</div>
                              <div className="text-muted text-sm" style={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {isPack ? `${(p.Items || []).length} items` : p.Description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`hs-badge ${isPack ? 'hs-badge-pack' : 'hs-badge-draft'}`}
                            style={isPack ? { background: '#7c3aed', color: '#fff' } : {}}>
                            {p.Category}
                          </span>
                        </td>
                        <td>
                          {!isPack && (
                            <div style={{ display: 'flex', gap: 4 }}>
                              {(p.FreeFrom || []).length > 0 && (
                                <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                  <span className="hs-badge" style={{ background: '#059669', color: '#fff', fontSize: 10 }}>✓ {p.FreeFrom.length}</span>
                                  <span className="tag-hover-popup">
                                    <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Recipe Doesn't Include</div>
                                    {p.FreeFrom.map(tag => (
                                      <span key={tag} className="hs-badge" style={{ background: '#059669', color: '#fff', marginRight: 3, marginBottom: 2, fontSize: 10 }}>✓ {tag}</span>
                                    ))}
                                  </span>
                                </span>
                              )}
                              {(p.Dietary || []).length > 0 && (
                                <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                  <span className="hs-badge" style={{ background: '#7c3aed', color: '#fff', fontSize: 10 }}>🌱 {p.Dietary.length}</span>
                                  <span className="tag-hover-popup">
                                    <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Dietary</div>
                                    {p.Dietary.map(tag => (
                                      <span key={tag} className="hs-badge" style={{ background: '#7c3aed', color: '#fff', marginRight: 3, marginBottom: 2, fontSize: 10 }}>🌱 {tag}</span>
                                    ))}
                                  </span>
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="text-right font-mono">£{(p.UnitPrice || 0).toFixed(2)}</td>
                        <td className="text-right">
                          {isPack
                            ? <span className="text-muted">—</span>
                            : avail !== undefined
                              ? <strong className={avail > 0 ? 'text-success' : 'text-danger'}>{avail}</strong>
                              : <span className="text-muted">—</span>
                          }
                        </td>
                      </tr>
                    );

                    // Nested child rows for expanded packs
                    const childRows = isExpanded ? (p.Items || []).map((item, idx) => {
                      const cp = item.Product;
                      return (
                        <tr key={`${p.ProductId}-item-${idx}`}
                          style={{ background: '#f8fafc', fontSize: 13 }}
                          className="clickable"
                          onClick={(e) => { e.stopPropagation(); if (cp) handleSelectProduct({ ...cp, _type: 'product' }); }}>
                          <td style={{ paddingLeft: 44 }}>
                            <span style={{ color: '#94a3b8', marginRight: 6 }}>└</span>
                            <strong>{cp?.ProductName || 'Unknown'}</strong>
                            <span className="text-muted text-sm" style={{ marginLeft: 8 }}>×{item.Quantity}</span>
                            <div className="text-muted text-sm font-mono" style={{ paddingLeft: 18 }}>{cp?.SKU || ''}</div>
                          </td>
                          <td><span className="hs-badge hs-badge-draft" style={{ fontSize: 10 }}>{cp?.Category || ''}</span></td>
                          <td>
                            {(cp?.FreeFrom || []).length > 0 && (
                              <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                <span className="hs-badge" style={{ background: '#059669', color: '#fff', fontSize: 9 }}>✓ {cp.FreeFrom.length}</span>
                                <span className="tag-hover-popup">
                                  <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Recipe Doesn't Include</div>
                                  {cp.FreeFrom.map(tag => (
                                    <span key={tag} className="hs-badge" style={{ background: '#059669', color: '#fff', marginRight: 2, marginBottom: 1, fontSize: 9 }}>✓ {tag}</span>
                                  ))}
                                </span>
                              </span>
                            )}
                          </td>
                          <td className="text-right font-mono text-muted">£{(cp?.UnitPrice || 0).toFixed(2)}</td>
                          <td className="text-right"><span className="text-muted">—</span></td>
                        </tr>
                      );
                    }) : [];

                    return [mainRow, ...childRows];
                  })}
                </tbody>
              </table>
            </div>
            {/* ---- PAGINATION CONTROLS ---- */}
            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
                <button className="hs-btn hs-btn-sm hs-btn-secondary" disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
                <span className="text-sm">
                  Page {page} of {pagination.totalPages}
                </span>
                <button className="hs-btn hs-btn-sm hs-btn-secondary" disabled={page >= pagination.totalPages}
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}>Next →</button>
              </div>
            )}
          </div>

          {/* ======== DETAIL PANEL ======== */}
          {selectedProduct && (
            <div>
              {/* Product / Pack detail card */}
              <div className="hs-card mb-md">
                <div className="hs-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h2 style={{ margin: 0 }}>
                    {selectedProduct._type === 'pack' && <span style={{ marginRight: 6 }}>📦</span>}
                    {selectedProduct.ProductName}
                    {selectedProduct._type === 'pack' && selectedProduct.PromoPack ? <span className="hs-badge" style={{ background: '#15803d', color: '#fff', fontSize: 11, marginLeft: 8, verticalAlign: 'middle' }}>🎁 Promo</span> : null}
                  </h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`hs-badge ${selectedProduct._type === 'pack' ? '' : 'hs-badge-draft'}`}
                      style={selectedProduct._type === 'pack' ? { background: '#7c3aed', color: '#fff' } : {}}>
                      {selectedProduct.Category}
                    </span>
                    <button className="hs-btn hs-btn-sm hs-btn-secondary"
                      onClick={() => setSelectedProduct(null)}
                      title="Close"
                      style={{ padding: '2px 8px', fontSize: 16, lineHeight: 1 }}>✕</button>
                  </div>
                </div>
                <div className="hs-card-body">
                  <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>{selectedProduct.Description}</p>

                  <div className="hs-form-row mb-md">
                    <div><span className="text-muted text-sm">SKU</span><br /><strong className="font-mono">{selectedProduct.SKU}</strong></div>
                    <div><span className="text-muted text-sm">SAP Code</span><br /><strong className="font-mono">{selectedProduct.SAPMaterialCode || '—'}</strong></div>
                    <div><span className="text-muted text-sm">Unit Price</span><br /><strong style={{ fontSize: 18 }}>£{(selectedProduct.UnitPrice || 0).toFixed(2)}</strong></div>
                  </div>

                  {/* ---- Pack Items Section ---- */}
                  {selectedProduct._type === 'pack' && (selectedProduct.Items || []).length > 0 && (
                    <div className="mb-md">
                      <span className="text-muted text-sm" style={{ display: 'block', marginBottom: 8 }}>Pack Contents ({selectedProduct.Items.length} items)</span>
                      <table className="hs-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr><th>Product</th><th>Qty</th><th>Recipe Doesn't Include</th><th className="text-right">Price</th></tr>
                        </thead>
                        <tbody>
                          {selectedProduct.Items.map((item, idx) => {
                            const cp = item.Product;
                            return (
                              <tr key={idx} className="clickable"
                                onClick={() => cp && handleSelectProduct({ ...cp, _type: 'product' })}>
                                <td>
                                  <strong>{cp?.ProductName || 'Unknown'}</strong>
                                  <div className="text-muted text-sm font-mono">{cp?.SKU || ''}</div>
                                </td>
                                <td>×{item.Quantity}</td>
                                <td>
                                  {(cp?.FreeFrom || []).map(tag => (
                                    <span key={tag} className="hs-badge" style={{
                                      background: '#059669', color: '#fff',
                                      marginRight: 2, fontSize: 9
                                    }}>✓ {tag}</span>
                                  ))}
                                </td>
                                <td className="text-right font-mono">£{(cp?.UnitPrice || 0).toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* ---- Product-only: Recipe doesn't include ---- */}
                  {selectedProduct._type !== 'pack' && (
                    <div className="mb-md">
                      <span className="text-muted text-sm">Recipe Doesn't Include</span><br />
                      {(selectedProduct.FreeFrom || []).length > 0
                        ? selectedProduct.FreeFrom.map(tag => (
                          <span key={tag} className="hs-badge" style={{
                            background: '#059669', color: '#fff',
                            marginRight: 4, marginTop: 4,
                          }}>✓ {tag}</span>
                        ))
                        : <span className="text-muted text-sm" style={{ marginTop: 4, display: 'inline-block' }}>—</span>
                      }
                    </div>
                  )}

                  {/* ---- Product-only: Ingredients ---- */}
                  {selectedProduct._type !== 'pack' && (
                    <div>
                      <span className="text-muted text-sm">Ingredients</span><br />
                      <span className="text-sm" style={{ lineHeight: 1.8 }}>
                        {(selectedProduct.Ingredients || []).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stock at user's site — hide for packs */}
              {selectedProduct._type !== 'pack' && <div className="hs-card mb-md">
                <div className="hs-card-header flex-between">
                  <h2>Stock{userSite ? ` — ${userSite.SiteName}` : ' by Site'}</h2>
                </div>
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  <table className="hs-table">
                    <thead>
                      <tr><th>Site</th><th>Available Stock</th></tr>
                    </thead>
                    <tbody>
                      {stockData.map(s => (
                        <tr key={s.StockId} style={userSite && s.SiteId === userSite.SiteId ? { background: '#f0fdfa', fontWeight: 600 } : {}}>
                          <td>{s.SiteName} <span className="text-muted text-sm">({s.SiteCode})</span></td>
                          <td><strong className={s.AvailableQty > 0 ? 'text-success' : 'text-danger'}>{s.AvailableQty}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>}

              {/* ======== SUBSTITUTES (TABBED) ======== */}
              {selectedProduct._type !== 'pack' &&
              <div className="hs-card mb-md">
                {/* Tab bar */}
                <div className="hs-card-header" style={{ padding: 0, borderBottom: 'none' }}>
                  <div style={{ display: 'flex', gap: 0 }}>
                    <button
                      style={{
                        flex: 1, padding: '10px 16px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
                        background: subTab === 'recommended' ? '#fff' : '#f8fafc',
                        color: subTab === 'recommended' ? 'var(--hs-orange)' : '#64748b',
                        borderBottom: subTab === 'recommended' ? '3px solid var(--hs-orange)' : '3px solid transparent',
                      }}
                      onClick={() => setSubTab('recommended')}>
                      Recommended
                    </button>
                    <button
                      style={{
                        flex: 1, padding: '10px 16px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
                        background: subTab === 'search' ? '#fff' : '#f8fafc',
                        color: subTab === 'search' ? 'var(--hs-orange)' : '#64748b',
                        borderBottom: subTab === 'search' ? '3px solid var(--hs-orange)' : '3px solid transparent',
                      }}
                      onClick={() => {
                        setSubTab('search');
                        setSubCriteria({
                          maxPriceDelta: 5, avoidAllergens: [], matchIngredients: [],
                          inStockOnly: true, category: selectedProduct.Category,
                        });
                      }}>
                      🔍 Search
                    </button>
                  </div>
                </div>

                {/* ---- Recommended tab ---- */}
                {subTab === 'recommended' && (
                  substitutions.length === 0 ? (
                    <div className="hs-card-body"><p className="text-muted">No recommended substitutes from Pimberly for this product</p></div>
                  ) : (
                    <table className="hs-table">
                      <thead>
                        <tr><th>#</th><th>Substitute</th><th>Recipe Doesn't Include</th><th>Price</th>{currentUser && <th>Stock</th>}<th></th></tr>
                      </thead>
                      <tbody>
                        {substitutions.map(s => (
                          <tr key={s.SubstituteCode}>
                            <td><span className="hs-badge hs-badge-draft">#{s.Priority}</span></td>
                            <td>
                              <strong style={{ cursor: s.SubstituteProduct?._pimberlyOnly ? 'default' : 'pointer', color: 'var(--hs-orange)' }}
                                onClick={(e) => { e.stopPropagation(); if (!s.SubstituteProduct?._pimberlyOnly) handleSelectProduct(s.SubstituteProduct); }}>
                                {s.SubstituteProduct?.ProductName}
                              </strong>
                              <div className="text-muted text-sm font-mono">{s.SubstituteCode}</div>
                              {s.SubstituteProduct?._pimberlyOnly && <span className="text-muted text-sm">(Pimberly only)</span>}
                            </td>
                            <td>
                              {(s.SubstituteProduct?.FreeFrom || []).map(tag => (
                                <span key={tag} className="hs-badge" style={{
                                  background: '#059669', color: '#fff',
                                  marginRight: 2, fontSize: 9
                                }}>{tag}</span>
                              ))}
                            </td>
                            <td className="font-mono">{s.SubstituteProduct?.UnitPrice != null ? `£${Number(s.SubstituteProduct.UnitPrice).toFixed(2)}` : '—'}</td>
                            {currentUser && (
                              <td>
                                {s.StockAtSite ? (
                                  <strong className={s.StockAtSite.AvailableQty > 0 ? 'text-success' : 'text-danger'}>
                                    {s.StockAtSite.AvailableQty}
                                  </strong>
                                ) : '—'}
                              </td>
                            )}
                            <td>
                              {!s.SubstituteProduct?._pimberlyOnly && (
                                <button className="hs-btn hs-btn-sm hs-btn-secondary"
                                  onClick={(e) => { e.stopPropagation(); handleAddToOrder(s.SubstituteProduct); }}>
                                  + Add
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}

                {/* ---- Search tab ---- */}
                {subTab === 'search' && (
                  <div className="hs-card-body">
                    <p className="text-muted text-sm mb-md">
                      Search all products for the best substitute match based on price, ingredients, allergens, and stock availability.
                    </p>

                    <div className="hs-form-row mb-md">
                      <div className="hs-form-group">
                        <label>Max Price Difference (£)</label>
                        <input className="hs-input" type="number" min="0" step="0.5"
                          value={subCriteria.maxPriceDelta}
                          onChange={e => setSubCriteria({ ...subCriteria, maxPriceDelta: parseFloat(e.target.value) || 0 })} />
                      </div>
                      <div className="hs-form-group">
                        <label>Category</label>
                        <select className="hs-select" value={subCriteria.category}
                          onChange={e => setSubCriteria({ ...subCriteria, category: e.target.value })}>
                          <option value="">Any Category</option>
                          {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="hs-form-group">
                        <label>
                          <input type="checkbox" checked={subCriteria.inStockOnly}
                            onChange={e => setSubCriteria({ ...subCriteria, inStockOnly: e.target.checked })}
                            style={{ marginRight: 6 }} />
                          In stock only
                        </label>
                      </div>
                    </div>

                    <div className="hs-form-group mb-md">
                      <label>Match Ingredients (comma-separated)</label>
                      <input className="hs-input" placeholder="e.g. chicken, lettuce, cheese"
                        value={subCriteria.matchIngredients.join(', ')}
                        onChange={e => setSubCriteria({
                          ...subCriteria,
                          matchIngredients: e.target.value ? e.target.value.split(',').map(i => i.trim()).filter(Boolean) : []
                        })} />
                    </div>

                    <button className="hs-btn hs-btn-primary" onClick={handleFindSubstitutes} disabled={subSearching}>
                      {subSearching ? 'Searching…' : '🔍 Find Best Matches'}
                    </button>

                    {subResults && (
                      <div style={{ marginTop: 16 }}>
                        <h3 style={{ margin: '0 0 8px' }}>
                          {subResults.totalCandidates} substitute{subResults.totalCandidates !== 1 ? 's' : ''} found
                        </h3>
                        {subResults.totalCandidates === 0 ? (
                          <div className="empty-state" style={{ padding: 20 }}>
                            <p>No products match all your criteria. Try widening the price range or removing allergen filters.</p>
                          </div>
                        ) : (
                          <div style={{ maxHeight: 350, overflow: 'auto' }}>
                            <table className="hs-table">
                              <thead>
                                <tr>
                                  <th>Score</th><th>Product</th><th>Allergens</th>
                                  <th>Price</th><th>Δ</th>{currentUser && <th>Stock</th>}<th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {subResults.substitutes.map(s => (
                                  <tr key={s.ProductId}>
                                    <td>
                                      <span className={`hs-badge ${s.RelevanceScore >= 60 ? 'hs-badge-available' : s.RelevanceScore >= 30 ? 'hs-badge-pending' : 'hs-badge-draft'}`}>
                                        {s.RelevanceScore}%
                                      </span>
                                      {s.HasSubstitutionRule && <span className="hs-badge hs-badge-pack" style={{ marginLeft: 4, fontSize: 9 }}>Rule</span>}
                                    </td>
                                    <td>
                                      <strong style={{ cursor: 'pointer', color: 'var(--hs-orange)' }}
                                        onClick={() => handleSelectProduct(s)}>
                                        {s.ProductName}
                                      </strong>
                                      <div className="text-muted text-sm font-mono">{s.SKU}</div>
                                    </td>
                                    <td>
                                      {(s.Allergens || []).length === 0
                                        ? null
                                        : (s.Allergens || []).map(a => (
                                          <span key={a} className="hs-badge" style={{
                                            background: ALLERGEN_COLOURS[a] || '#64748b', color: '#fff',
                                            marginRight: 2, fontSize: 9,
                                          }}>{a}</span>
                                        ))
                                      }
                                    </td>
                                    <td className="font-mono">£{s.UnitPrice.toFixed(2)}</td>
                                    <td>
                                      <span className={s.PriceDifference > 0 ? 'text-danger' : s.PriceDifference < 0 ? 'text-success' : ''}>
                                        {s.PriceDifference > 0 ? '+' : ''}{s.PriceDifference !== 0 ? `£${s.PriceDifference.toFixed(2)}` : '—'}
                                      </span>
                                    </td>
                                    {currentUser && (
                                      <td>
                                        {s.StockAtSite ? (
                                          <strong className={s.StockAtSite.AvailableQty > 0 ? 'text-success' : 'text-danger'}>
                                            {s.StockAtSite.AvailableQty}
                                          </strong>
                                        ) : '—'}
                                      </td>
                                    )}
                                    <td>
                                      <button className="hs-btn hs-btn-sm hs-btn-primary"
                                        onClick={() => handleAddToOrder(s)}>+ Add</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
